import {useParams} from "react-router-dom";
import {RouteParam} from "../../types/types";
import React, {useCallback, useEffect, useState} from "react";
import {VerificationViewModel} from "../../viewmodel/verification/VerificationViewModel";
import './Information.scss'
import {RsAddress, RsDocument, RsPerson} from "../../api/dto/RsVerification";
import {VerifyService} from "../../service/VerifyService";
import {RqInputInformation} from "../../api/dto/RqInputInformation";
import {useStepCheck} from "../../hooks/stepcheck/useStepCheck";
import {HeaderComponent} from "../../components/common/header/HeaderComponent";
import {PersonalInformationViewModel} from "../../viewmodel/information/PersonalInformationViewModel";
import {AddressInformationViewModel} from "../../viewmodel/information/AddressInformationViewModel";
import {DocumentInformationViewModel} from "../../viewmodel/information/DocumentInformationViewModel";
import {ConfirmInformationViewModel} from "../../viewmodel/information/ConfirmInformationViewModel";
import {useSubmit} from "../../hooks/submit/useSubmit";
import {DocumentType} from "../../domain/verification/DocumentType";

function initialPageFromFlags(personalExcluded: boolean, addressExcluded: boolean, documentExcluded: boolean) {
    if (!personalExcluded) return 0;
    if (!addressExcluded) return 1;
    if (!documentExcluded) return 2;
    return 3;
}

function nextPageFrom(current: number, personalExcluded: boolean, addressExcluded: boolean, documentExcluded: boolean) {
    switch (current) {
        case -1:
            return initialPageFromFlags(personalExcluded, addressExcluded, documentExcluded);
        case 0:
            return addressExcluded ? (documentExcluded ? 3 : 2) : 1;
        case 1:
            return documentExcluded ? 3 : 2;
        case 2:
            return 3;
        default:
            return current;
    }
}

function prevPageFrom(current: number, addressExcluded: boolean, documentExcluded: boolean) {
    switch (current) {
        case 1:
            return 0;
        case 2:
            return addressExcluded ? 0 : 1;
        case 3:
            if (documentExcluded) return addressExcluded ? 0 : 1;
            return 2;
        default:
            return current;
    }
}

const InformationPage: React.FC = () => {

    const [currentPage, setCurrentPage] = useState(-1)
    const [personalEditExcluded, setPersonalEditExcluded] = useState(false)
    const [addressEditExcluded, setAddressEditExcluded] = useState(false)
    const [documentEditExcluded, setDocumentEditExcluded] = useState(false)

    const { getVerification } = VerificationViewModel()
    const { verificationId } = useParams<RouteParam>()
    const { evaluateUrl } = useStepCheck(verificationId)
    const { submit } = useSubmit(verificationId)
    const {
        personEditedInformation,
        personalInformationFieldsAsObject,
        buildPersonalInfoBlock,
        populatePersonInformation
    } = PersonalInformationViewModel(() => onNextClick())
    const {
        addressEditedInformation,
        addressFieldsAsObject,
        buildAddressInfoBlock,
        populateAddressInformation
    } = AddressInformationViewModel(() => onNextClick())
    const {
        documentEditedInformation,
        documentFieldsAsObject,
        buildFieldsDocumentInfoBlock,
        populateDocumentInformation
    } = DocumentInformationViewModel(() => onNextClick())
    const {
        buildConfirmInfoBlock
    } = ConfirmInformationViewModel(() => onSubmit())
    const [dataLoaded, setDataLoaded] = useState(false)
    const [documentType, setDocumentType] = useState(DocumentType.OTHER)


    const populateInitialInformation = useCallback((person: RsPerson, document: RsDocument, address: RsAddress | undefined) => {
        populatePersonInformation(person)
        populateDocumentInformation(document)
        populateAddressInformation(address)
        setDataLoaded(true)
    }, [populatePersonInformation, populateDocumentInformation, populateAddressInformation])

    useEffect(() => {
        evaluateUrl()
        if (!verificationId) return
        getVerification(verificationId).then(result => {
            populateInitialInformation(result.person, result.document, result.address)
            setPersonalEditExcluded(result.personalEditExcluded)
            setAddressEditExcluded(result.addressEditExcluded)
            setDocumentEditExcluded(result.documentEditExcluded)
            setDocumentType(result.document.type)
            const initial = initialPageFromFlags(
                result.personalEditExcluded,
                result.addressEditExcluded,
                result.documentEditExcluded
            );
            setCurrentPage(initial);
        })

    }, [getVerification, populateInitialInformation, evaluateUrl, verificationId]);

    const onNextClick = () => {
        setCurrentPage(prev => nextPageFrom(prev, personalEditExcluded, addressEditExcluded, documentEditExcluded))
    }

    const onBackClick = () => {
        setCurrentPage(prev => prevPageFrom(prev, addressEditExcluded, documentEditExcluded))
    }

    const onSubmit = async () => {
        submit(sendInputInformation)
    }

    const sendInputInformation = async () => {
        const inputInformation = new RqInputInformation(
            personEditedInformation,
            documentEditedInformation,
            addressEditedInformation
        )
        await VerifyService.getInstance().sendInputInformation(inputInformation, verificationId!!)
        window.location.href = `${window.location.protocol}//${window.location.host}/${verificationId}/complete`
    }

    const buildHeader = () => {
        if (currentPage === 0) {
            return <HeaderComponent
                title="Confirm information"
                subtitle="Please review the information below for accuracy and make any necessary updates."
                progress={70}
                spacer
            />
        } else if (currentPage === 1) {
            return <HeaderComponent
                back={!personalEditExcluded}
                onBackClick={() => onBackClick()}
                progress={80}
            />
        } else if ((currentPage === 2 && documentType.toLowerCase() === DocumentType.NO_ID) || currentPage === 3) {
            return <HeaderComponent
                title="Review details"
                subtitle="Please ensure all information is correct before submitting."
                back={!personalEditExcluded || !addressEditExcluded || !documentEditExcluded}
                onBackClick={() => onBackClick()}
                progress={100}
                spacer
            />
        } else {
            return <HeaderComponent
                back={!personalEditExcluded || !addressEditExcluded}
                onBackClick={() => onBackClick()}
                progress={90}
            />
        }
    }

    return (
        <div className={`background-information background-white general-container ${currentPage !== 0 ? 'header-back-container' : ''}`}>
            {buildHeader()}
            {dataLoaded ? (
                <>
                    <div>
                        {!personalEditExcluded && buildPersonalInfoBlock(currentPage === 0)}
                        {!addressEditExcluded && buildAddressInfoBlock(currentPage === 1)}
                        {!documentEditExcluded && buildFieldsDocumentInfoBlock(documentType.toLowerCase() !== DocumentType.NO_ID && currentPage === 2)}
                        {
                            buildConfirmInfoBlock(
                                personalInformationFieldsAsObject(),
                                addressFieldsAsObject(),
                                documentFieldsAsObject(),
                                (documentType.toLowerCase() === DocumentType.NO_ID && currentPage === 2) || currentPage === 3)
                        }
                    </div>
                </>
            ) : ('Loading info')}

        </div>

    )

}

export default InformationPage